import { useLiveQuery } from '@tanstack/react-db'
import { useRouter } from '@tanstack/react-router'
import type { Callout, StatusChangeLog } from '@/lib/schemas/db'
import { useDb } from '@/lib/dbContext'
import type { BookingRow, CalloutStatus, ServiceType } from '@/components/BookingCard'
import { BookingsScreen } from '@/screens/BookingsScreen'
import type { NewCalloutInitialValues } from '@/screens/NewCalloutScreen'
import type { NextCalloutsMobileItem } from '@/screens/NextCalloutsMobileScreen'
import { fromZaLocalToUtcIso, fromUtcIsoToZaParts } from '@/lib/datetime'
import { withSpan } from '@/lib/sentry'
import { useNewCalloutModal } from '@/components/NewCalloutModalProvider'
import { useNewSessionModal } from '@/components/NewSessionModalProvider'

function mapCalloutStatus(status: Callout['status']): CalloutStatus {
  switch (status) {
    case 'New':
      return 'New'
    case 'Booked':
      return 'Booked'
    case 'LeftSite':
      return 'LeftSite'
    case 'Arrived':
      return 'Arrived'
    case 'Review':
      return 'Review'
    case 'ReadyToInvoice':
      return 'ReadyToInvoice'
    case 'Invoiced':
      return 'Invoiced'
    case 'Cancelled':
      return 'Cancelled'
    default:
      return 'New'
  }
}

function toBookingRow(
  callout: Callout,
  statusChangesByCallout: Map<string, StatusChangeLog[]>,
  serviceType: ServiceType,
  customerPublicIdById: Map<string, string>,
): BookingRow {
  const { day, time } = fromUtcIsoToZaParts(callout.startTime)

  return {
    id: callout.publicId ?? callout.id,
    privateId: callout.id,
    date: day,
    time: callout.isWholeDay ? '' : time,
    startTime: callout.startTime,
    endTime: callout.endTime,
    wholeDay: callout.isWholeDay,
    customerName: callout.contactName ?? '',
    customerId: callout.customerId,
    customerPublicId: customerPublicIdById.get(callout.customerId),
    issueSummary: callout.issueSummary ?? '',
    serviceType,
    sessionSubtype: callout.sessionSubtype,
    calloutStatus: mapCalloutStatus(callout.status),
    contactAddress: callout.contactAddress,
    contactPhone: callout.contactPhone,
    contactEmail: callout.contactEmail,
    invoiceName: callout.invoiceName,
    invoiceVat: callout.invoiceVat,
    invoiceAddress: callout.invoiceAddress,
    invoiceEmail: callout.invoiceEmail,
    statusChanges: statusChangesByCallout.get(callout.id) ?? [],
  }
}

export function BookingsScreenContainer() {
  const db = useDb()
  const router = useRouter()
  const currentUser = db.getCurrentUser()
  const { openNewCalloutModal } = useNewCalloutModal()
  const { openNewSessionModal } = useNewSessionModal()
  const result = useLiveQuery((q) =>
    q.from({ callouts: db.calloutsCollection }),
  )
  const statusChangesResult = useLiveQuery((q) =>
    q.from({ statusChanges: db.statusChangesCollection }),
  )
  const sessionsResult = useLiveQuery((q) =>
    q.from({ sessions: db.sessionsCollection }),
  )
  const customersResult = useLiveQuery((q) =>
    q.from({ customers: db.customersCollection }),
  )
  const remoteStatusChangesResult = useLiveQuery((q) =>
    q.from({ statusChanges: db.sessionStatusChangesCollection }),
  )

  if (
    result.isLoading ||
    statusChangesResult.isLoading ||
    sessionsResult.isLoading ||
    remoteStatusChangesResult.isLoading ||
    customersResult.isLoading
  ) {
    return (
      <div className="bg-slate-50 text-slate-900">
        <main className="max-w-4xl mx-auto px-4 py-6 space-y-2">
          <h1 className="text-2xl font-semibold">Bookings</h1>
          <p className="text-sm text-slate-600">Loading bookings…</p>
        </main>
      </div>
    )
  }

  if (
    result.isError ||
    statusChangesResult.isError ||
    sessionsResult.isError ||
    remoteStatusChangesResult.isError ||
    customersResult.isError
  ) {
    return (
      <div className="bg-slate-50 text-slate-900">
        <main className="max-w-4xl mx-auto px-4 py-6 space-y-2">
          <h1 className="text-2xl font-semibold">Bookings</h1>
          <p className="text-sm text-slate-600">
            Unable to load bookings right now.
          </p>
        </main>
      </div>
    )
  }

  const statusChangesByCallout = new Map<string, StatusChangeLog[]>()
  for (const change of statusChangesResult.data ?? []) {
    const current = statusChangesByCallout.get(change.serviceId) ?? []
    current.push(change)
    statusChangesByCallout.set(change.serviceId, current)
  }

  const statusChangesBySession = new Map<string, StatusChangeLog[]>()
  for (const change of remoteStatusChangesResult.data ?? []) {
    const current = statusChangesBySession.get(change.serviceId) ?? []
    current.push(change)
    statusChangesBySession.set(change.serviceId, current)
  }

  const customerPublicIdById = new Map<string, string>()
  for (const customer of customersResult.data ?? []) {
    if (customer.publicId) customerPublicIdById.set(customer.id, customer.publicId)
  }

  const activeCallouts =
    result.data?.filter((callout) => callout.status !== 'Cancelled') ?? []
  const activeSessions =
    sessionsResult.data?.filter((session) => session.status !== 'Cancelled') ?? []

  const bookings: BookingRow[] = [
    ...activeCallouts.map((callout) =>
      toBookingRow(callout, statusChangesByCallout, 'Callout', customerPublicIdById),
    ),
    ...activeSessions.map((session) =>
      toBookingRow(session, statusChangesBySession, 'Session', customerPublicIdById),
    ),
  ]

  // Booking rows carry public ids, but the sessions collection is keyed by
  // private id — so route-by-type checks must recognise both forms, or every
  // Session falls through to the callout routes (JC-079).
  const sessionIds = new Set(
    (sessionsResult.data ?? []).flatMap((session) =>
      session.publicId ? [session.id, session.publicId] : [session.id],
    ),
  )
  const isSessionId = (id: string) =>
    sessionIds.has(id) || Boolean(db.sessionsCollection.get?.(id))

  const nextCallouts: NextCalloutsMobileItem[] = [
    ...activeCallouts.map((callout) => ({
      id: callout.publicId ?? callout.id,
      privateId: callout.id,
      customerName: callout.contactName ?? '',
      customerId: callout.customerId,
      customerPublicId: customerPublicIdById.get(callout.customerId),
      issueSummary: callout.issueSummary ?? '',
      status: callout.status,
      serviceType: 'Callout' as const,
      contactAddress: callout.contactAddress ?? '',
      startTime: callout.startTime,
      endTime: callout.endTime,
      isWholeDay: callout.isWholeDay,
      statusChanges: statusChangesByCallout.get(callout.id) ?? [],
    })),
    ...activeSessions.map((session) => ({
      id: session.publicId ?? session.id,
      privateId: session.id,
      customerName: session.contactName ?? '',
      customerId: session.customerId,
      customerPublicId: customerPublicIdById.get(session.customerId),
      issueSummary: session.issueSummary ?? '',
      status: session.status,
      serviceType: 'Session' as const,
      sessionSubtype: session.sessionSubtype,
      contactAddress: session.contactAddress ?? '',
      startTime: session.startTime,
      endTime: session.endTime,
      isWholeDay: session.isWholeDay,
      statusChanges: statusChangesBySession.get(session.id) ?? [],
    })),
  ]

  const toTimestampsFromFormValues = (
    values: NewCalloutInitialValues,
  ): { startTime: string; endTime: string; wholeDay: boolean } => {
    const wholeDay = values.wholeDay ?? false
    const day = values.day
    const startTimePart = values.time && !wholeDay ? values.time : '09:00'
    const startTime = fromZaLocalToUtcIso(day, startTimePart)

    let endTime: string
    if (values.endTime && !wholeDay) {
      endTime = fromZaLocalToUtcIso(day, values.endTime)
    } else if (wholeDay) {
      endTime = fromZaLocalToUtcIso(day, '23:59')
    } else {
      const [hourStr, minuteStr] = startTimePart.split(':')
      const hour = Number(hourStr ?? '9')
      const endHour = String(Math.min(23, hour + 1)).padStart(2, '0')
      endTime = fromZaLocalToUtcIso(day, `${endHour}:${minuteStr ?? '00'}`)
    }

    return { startTime, endTime, wholeDay }
  }

  const handleCreateCallout = async (
    values: NewCalloutInitialValues,
  ) =>
    withSpan(
      {
        op: 'callout.create',
        name: 'Create callout from bookings',
      },
      async () => {
        const { startTime, endTime, wholeDay } =
          toTimestampsFromFormValues(values)

        let customerId = values.customerId

        if (!customerId && values.customerName.trim().length > 0) {
          const customer = await withSpan(
            {
              op: 'customer.create',
              name: 'Create customer during callout booking',
            },
            () =>
              db.createCustomerAndWait({
                fullName: values.customerName,
                externalId: '',
                contactAddress: values.address,
                contactEmail: values.email,
                contactPhone: values.phone,
                invoiceName: values.invoiceTo || values.customerName,
                invoiceAddress:
                  values.invoiceAddress ?? values.address,
                invoiceEmail:
                  values.invoiceEmail ?? values.email,
                invoicePhone: values.phone,
                invoiceVat: values.vat ?? '',
              }),
          )
          customerId = customer.id
        }

        const callout = await db.createCalloutAndWait({
          customerId: customerId ?? '',
          startTime,
          endTime,
          isWholeDay: wholeDay,
          status: 'Booked',
          issueSummary: values.issueSummary,
          contactAddress: values.address,
          contactName: values.customerName,
          contactPhone: values.phone,
          contactEmail: values.email,
          invoiceName: values.invoiceTo || values.customerName,
          invoiceVat: values.vat ?? '',
          invoiceAddress:
            values.invoiceAddress ?? values.address,
          invoiceEmail:
            values.invoiceEmail ?? values.email,
          invoicePhone: values.phone,
          invoiceId: undefined,
          billablesConsumed: [],
          auditLogs: [],
          statusChanges: [],
        })

        if (values.notes && values.notes.trim().length > 0) {
          await withSpan(
            {
              op: 'callout.note.add',
              name: 'Add initial booking note',
            },
            async () => {
              const { transaction } = db.appendLogWithTransaction(
                callout.id,
                { text: values.notes },
              )
              await transaction.isPersisted.promise
            },
          )
        }
      },
    )

  const handleUpdateCallout = async (
    bookingId: string,
    values: NewCalloutInitialValues,
  ) => {
    const { startTime, endTime, wholeDay } =
      toTimestampsFromFormValues(values)

    const patch = {
      startTime,
      endTime,
      isWholeDay: wholeDay,
      issueSummary: values.issueSummary,
      contactAddress: values.address,
      contactName: values.customerName,
      contactPhone: values.phone,
      contactEmail: values.email,
      invoiceName: values.invoiceTo,
      invoiceVat: values.vat ?? '',
      invoiceAddress: values.invoiceAddress ?? values.address,
      invoiceEmail: values.invoiceEmail ?? values.email,
      sessionSubtype: values.sessionSubtype,
    }

    if (db.calloutsCollection.get?.(bookingId)) {
      await db.updateCalloutAndWait(bookingId, patch)
      return
    }

    if (db.sessionsCollection.get?.(bookingId)) {
      await db.sessionsCollection
        .update(bookingId, (draft) => {
          Object.assign(draft, patch)
          draft.updatedAt = new Date().toISOString()
        })
        .isPersisted.promise
    }
  }

  const handleMarkCalloutReadyToInvoice = (bookingId: string) =>
    withSpan(
      {
        op: 'callout.status.change',
        name: 'Mark callout ready to invoice (bookings)',
        attributes: { bookingId, nextStatus: 'ReadyToInvoice' },
      },
      () =>
        db.updateCalloutAndWait(bookingId, {
          status: 'ReadyToInvoice',
        }),
    )

  const handleCancelCallout = async (bookingId: string) => {
    if (db.sessionsCollection.get?.(bookingId)) {
      return withSpan(
        {
          op: 'remote-session.status.change',
          name: 'Cancel session (bookings)',
          attributes: { bookingId, nextStatus: 'Cancelled' },
        },
        async () => {
          const session = db.sessionsCollection.get?.(bookingId)
          if (!session) return

          if (session.status !== 'Cancelled') {
            db.appendSessionStatusChange(bookingId, {
              previousStatus: session.status,
              newStatus: 'Cancelled',
              reason: 'Cancelled from bookings',
            })
          }

          await db.sessionsCollection
            .update(bookingId, (draft) => {
              draft.status = 'Cancelled'
              draft.updatedAt = new Date().toISOString()
            })
            .isPersisted.promise
        },
      )
    }

    return withSpan(
      {
        op: 'callout.status.change',
        name: 'Cancel callout (bookings)',
        attributes: { bookingId, nextStatus: 'Cancelled' },
      },
      () =>
        db.updateCalloutAndWait(bookingId, {
          status: 'Cancelled',
          statusReason: 'Cancelled from bookings',
        }),
    )
  }

  return (
    <BookingsScreen
      bookings={bookings}
      nextCallouts={nextCallouts}
      onNewCalloutClick={() => openNewCalloutModal()}
      onNewSessionClick={() => openNewSessionModal()}
      onCreateCalloutAtDate={(date) => openNewCalloutModal(date)}
      onCreateSessionAtDate={(date) => openNewSessionModal(date)}
      onCreateCallout={handleCreateCallout}
      onUpdateCallout={handleUpdateCallout}
      onMarkCalloutReadyToInvoice={handleMarkCalloutReadyToInvoice}
      onCancelCallout={handleCancelCallout}
      onSelectCallout={(id) => {
        if (isSessionId(id)) {
          router.navigate({
            to: '/sessions/$id',
            params: { id },
          })
          return
        }

        router.navigate({
          to: '/callouts/$id',
          params: { id },
        })
      }}
      onViewCallout={(id) => {
        if (isSessionId(id)) {
          router.navigate({
            to: '/sessions/$id',
            params: { id },
          })
          return
        }

        router.navigate({
          to: '/callouts/$id',
          params: { id },
        })
      }}
      onLogWork={(id) => {
        if (isSessionId(id)) {
          router.navigate({
            to: '/sessions/$id/logs',
            params: { id },
          })
          return
        }

        router.navigate({
          to: '/callouts/$id/logs',
          params: { id },
        })
      }}
      onPrepInvoice={(id) => {
        if (isSessionId(id)) {
          router.navigate({
            to: '/sessions/$id/invoice',
            params: { id },
          })
          return
        }

        router.navigate({
          to: '/callouts/$id/invoice',
          params: { id },
        })
      }}
      getAuthorDisplayName={db.getAuthorDisplayName}
      storageScope={currentUser?.id ? `user:${currentUser.id}` : undefined}
      onChangeStatus={(id, nextStatus) =>
        withSpan(
          {
            op: 'callout.status.change',
            name: 'Change callout status (bookings)',
            attributes: { serviceId: id, nextStatus },
          },
          async () => {
            if (db.calloutsCollection.get?.(id)) {
              await db.updateCalloutAndWait(id, {
                status: nextStatus,
                statusReason:
                  nextStatus === 'Review' || nextStatus === 'Cancelled'
                    ? `Changed from bookings to ${nextStatus}`
                    : undefined,
              })
              return
            }

            const session = db.sessionsCollection.get?.(id)
            if (!session) return

            if (session.status !== nextStatus) {
              db.appendSessionStatusChange(id, {
                previousStatus: session.status,
                newStatus: nextStatus,
                reason:
                  nextStatus === 'Review' || nextStatus === 'Cancelled'
                    ? `Changed from bookings to ${nextStatus}`
                    : undefined,
              })
            }

            await db.sessionsCollection
              .update(id, (draft) => {
                draft.status = nextStatus
                draft.updatedAt = new Date().toISOString()
              })
              .isPersisted.promise
          },
        )
      }
    />
  )
}
